import { Alert, AppAPI } from '@moderepo/mode-apis';
import { EntityClass, ThresholdComparisonOperator, AlertSeverity, EntityTarget, AlertConditionType, AlertState, FetchAlertsFilters,
    AlertRecipientType } from '@moderepo/mode-internal-apis';


/**
 * Given an entityClass name, find and return the displayName for that entityClass if it exist. If not, return the entityClass name as is.
 */
export const getEntityClassDisplayValue = (entityClassName: string, allEntityClasses: readonly EntityClass[] | undefined): string => {
    return allEntityClasses?.find((entityClass) => {
        return entityClass.entityClass === entityClassName;
    })?.displaySettings.name ?? entityClassName;
};



/**
 * Get the user friendly name for alert condition type
 */
export const getAlertConditionTypeDisplayName = (type: AlertConditionType): string => {
    const names = Object.freeze({
        [AlertConditionType.METRIC_HEARTBEAT]: 'alerting.alert_condition_type.metrics_heartbeat',
        [AlertConditionType.METRIC_THRESHOLD]: 'alerting.alert_condition_type.metrics_threshold',
    });

    return names[type];
};


/**
 * Get the user friendly name for alert severity type
 */
export const getAlertSeverityDisplayName = (type: AlertSeverity): string => {
    const names = Object.freeze({
        [AlertSeverity.LOW]     : 'alerting.alert_severity_level.low',
        [AlertSeverity.MODERATE]: 'alerting.alert_severity_level.moderate',
        [AlertSeverity.HIGH]    : 'alerting.alert_severity_level.high',
    });

    return names[type];
};



/**
 * Get the user friendly name for alert entity target type
 */
export const getAlertTargetByDisplayName = (type: EntityTarget): string => {
    const names = Object.freeze({
        [EntityTarget.PARENTS] : 'alerting.entity_target_type.parents',
        [EntityTarget.ENTITIES]: 'alerting.entity_target_type.entities',
    });

    return names[type];
};



/**
 * Get the user friendly name for alert threshold operator
 */
export const getThresholdComparisonOperatorDisplayName = (type: ThresholdComparisonOperator): string => {
    const names = Object.freeze({
        [ThresholdComparisonOperator.LESS_THAN]               : 'alerting.threshold_comparison_operator.less_than',
        [ThresholdComparisonOperator.LESS_THAN_OR_EQUAL_TO]   : 'alerting.threshold_comparison_operator.less_than_or_equal_to',
        [ThresholdComparisonOperator.IN]                      : 'alerting.threshold_comparison_operator.in',
        [ThresholdComparisonOperator.NOT_IN]                  : 'alerting.threshold_comparison_operator.not_in',
        [ThresholdComparisonOperator.GREATER_THAN_OR_EQUAL_TO]: 'alerting.threshold_comparison_operator.greater_than_or_equal_to',
        [ThresholdComparisonOperator.GREATER_THAN]            : 'alerting.threshold_comparison_operator.greater_than',
        [ThresholdComparisonOperator.BETWEEN]                 : 'alerting.threshold_comparison_operator.between',
        [ThresholdComparisonOperator.OUTSIDE]                 : 'alerting.threshold_comparison_operator.outside',
    });

    return names[type];
};



/**
 * Get the user friendly name for alert state
 */
export const getAlertStateDisplayName = (type: AlertState): string => {
    const names = Object.freeze({
        [AlertState.ALERTING]   : 'alerting.alert_state.alerting',
        [AlertState.INEFFECTIVE]: 'alerting.alert_state.ineffective',
        [AlertState.OBSOLETE]   : 'alerting.alert_state.obsolete',
        [AlertState.RECOVERED]  : 'alerting.alert_state.recovered',
    });

    return names[type];
};


/**
 * Get the user friendly name for alert action recipient type
 * TODO - Add other recipient types e.g. Slack, etc...
 */
export const getAlertRecipientTypeDisplayName = (type: AlertRecipientType): string => {
    const names = Object.freeze({
        [AlertRecipientType.EMAIL]: 'alerting.recipient_type.email',
        [AlertRecipientType.EVENT]: 'alerting.recipient_type.event',
    });

    return names[type];
};



/**
 * Fetch all alerts based on the given filters.
 * NOTE: The GET alerts API doesn't return all alerts, we need to make multiple calls to fetch 1 page at a time.
 */
export const getAllAlerts = async (
    projectId: number,
    filters: FetchAlertsFilters,
): Promise<readonly Alert[]> => {

    const result: Alert[] = [];

    // To keep track of the page we fetched
    const pageLimit = 100;
    let skip = 0;

    // A counter to prevent infinite loops
    let iterationCount = 0;

    // Instead of making this an infinite loop 'while (true)', we will run it until iterationCount hit 100. If the loop iterate this many times,
    // there is a chance that something unexpected happened and there is an infinite loop issue so we should stop it.
    while (iterationCount < 100) {
        // eslint-disable-next-line no-await-in-loop
        const response = await AppAPI.getInstance().getAlerts(projectId, {
            ...filters,
            limit: pageLimit,
            skip,
        });

        result.push(...response.items);

        // Stop the loop when # of items returned is less than # of items requested OR if the range returned by the backend is at the end
        if (!response.items || response.items.length < pageLimit || (response.range && response.range.end >= response.range.total)) {
            break;
        }

        skip += pageLimit;
        iterationCount += 1;
    }

    return result;
};
